import { ELIGIBILITY_GRID, getEligibleNewsLetters, getCompleteEligibleNewsLetters } from './eligibilityGrid';

// ─── Constants ────────────────────────────────────────────────────────────────

const MKNEWS = 'MKNEWS';
const GONOFF = 'GONOFF';
const AMMKTG = 'AMMKTG';
const ZYNC_COMM_CODES = new Set(['ZYNCMU', 'ZYNCPN', 'ZYNCSY', 'ZYNCSO']);
const SPECIAL_COMM_CODES = new Set(['DSKBCC', 'CSTMEM', MKNEWS]);
const OLD_NEW_COMM_CODES_MAPPING = Object.freeze({
  MKTRVL: 'MKMEMB',
  MKSHOP: 'MKMEMB',
  MKDENT: 'MKMEMB',
  MKOTHR: 'MKMEMB',
  MKCBEN: 'MKMEMB',
  MKPUBL: 'MKMEMB',
  MKFINS: 'MKOFFR',
  MKACQU: 'MKOFFR',
  MKCSER: 'MKOFFR',
  USCSNL: MKNEWS,
  RWDWAT: MKNEWS,
  CENTEX: MKNEWS,
  BGRINS: MKNEWS,
  OPBUS0: MKNEWS
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatOptioTimestamp = () => {
  const now = new Date();
  const pad = (n, width = 2) => n.toString().padStart(width, '0');
  const datePart = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const timePart = `${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(now.getSeconds())}`;
  const microseconds = `${pad(now.getMilliseconds(), 3)}000`;
  return `${datePart}-${timePart}.${microseconds}`;
};

// ─── Function 1: filterEligibleCommCodes ─────────────────────────────────────

// export const filterEligibleCommCodes = (codes = []) =>
//   codes.filter((code) => !EXCLUDED_COMM_CODES.has(code));

// ─── Function 2: normalizeCommCodes ──────────────────────────────────────────
/**
 * Three-pass normalization (mirrors Angular lines 150–180):
 *   Pass 1: DSKBCC / CSTMEM / MKNEWS → collapse to single MKNEWS
 *   Pass 2: apply oldNewCommCodesMapping
 *   Pass 3: deduplicate, preserve order
 */
export function normalizeCommCodes(inputCodes, onLog = undefined) {
  const codes = inputCodes || [];
  const normalizedCodes = [];
  codes.forEach(rawCode => {
    let code = rawCode.trim();
    if (SPECIAL_COMM_CODES.has(code)) {
      if (code !== MKNEWS && typeof onLog === 'function') {
        onLog(`found ${code} replaced with const commCode ${MKNEWS}`);
      }
      code = MKNEWS;
    }
    code = OLD_NEW_COMM_CODES_MAPPING[code] || code;
    if (!normalizedCodes.includes(code)) {
      normalizedCodes.push(code);
    }
  });
  const selectedCommCode = normalizedCodes[0] || '';
  return {
    multiCommCodes: normalizedCodes,
    selectedCommCode,
    isGONOFF: normalizedCodes.includes(GONOFF),
    isAMMKTG: codes.includes(AMMKTG),
    hasZyncCodes: normalizedCodes.some(code => ZYNC_COMM_CODES.has(code))
  };
}

// ─── Function 3: parseCommFromParams ─────────────────────────────────────────

export function parseCommFromParams(commString = '') {
  if (!commString) return [];
  return commString.split('-').map(c => c.trim()).filter(Boolean);
}

// ─── Function 4: getCommType ──────────────────────────────────────────────────

export const getCommType = (commCode, masterData) => {
  var _masterData$commCode$;
  if (!commCode || !(masterData !== null && masterData !== void 0 && masterData[commCode])) return null;
  const type = (_masterData$commCode$ = masterData[commCode].COMMUN_TYPE_CD) === null || _masterData$commCode$ === void 0 ? void 0 : _masterData$commCode$.trim();
  if (!type) return null;
  return {
    rawType: type,
    isSubscription: type.startsWith('S'),
    isProduct: type.startsWith('P')
  };
};

// ─── Function 5: processCommCodes ────────────────────────────────────────────
/**
 * Must read params.comm and uppercase it
 *   commCodeFromQueryStr = requestObject.comm.toUpperCase();
 */
export function processCommCodes(params, onLog = undefined) {
  var _params$comm;
  const comm = params === null || params === void 0 || (_params$comm = params.comm) === null || _params$comm === void 0 ? void 0 : _params$comm.toUpperCase(); // ← was: const comm = 'MKFINS';

  if (!comm) {
    return {
      error: 'No comm codes present'
    };
  }
  const inputCodes = parseCommFromParams(comm);
  if (inputCodes.length === 0) {
    return {
      error: 'No comm codes present after split'
    };
  }
  const result = normalizeCommCodes(inputCodes, onLog);

  // Angular line 117: AMMKTG check on first code → globalOptOut
  if (result.selectedCommCode === AMMKTG) {
    return {
      redirect: 'globalOptOut',
      ...result
    };
  }

  // Angular line 213: GONOFF check (Angular does this after decrypt; we check here
  // because React decrypts in the useOccEmail hook before this function is called)
  if (result.selectedCommCode === GONOFF) {
    return {
      redirect: 'occLogin',
      ...result
    };
  }
  return {
    ...result
  };
}

// ─── Function 6: filterByMasterData ──────────────────────────────────────────
/**
 * NEW — mirrors Angular lines 269–285.
 * Only comm codes that exist in the master data hash are kept.
 * This is the first gate before the GPC call.
 */
export function filterByMasterData(masterData, commCodes = []) {
  return (commCodes === null || commCodes === void 0 ? void 0 : commCodes.filter(code => !!(masterData !== null && masterData !== void 0 && masterData[code]))) || [];
}

// ─── Function 7: mergeMasterWithGPC (S flow) ─────────────────────────────────
/**
 * Used for S (Subscription) flow.
 * Matches GPC preference status onto master-data-validated comm codes.
 * GPC REST API fields: preferenceCode /
 *  preferenceStatus  (vs. Angular SOAP: prefCD / explctPrefStatus)
 */
export const mergeMasterWithGPC = (masterData, gpcResponse, commCodes = []) => {
  var _gpcResponse$entityPr;
  if (!masterData || !gpcResponse) return [];
  const gpcList = (gpcResponse === null || gpcResponse === void 0 || (_gpcResponse$entityPr = gpcResponse.entityPreferenceResponse) === null || _gpcResponse$entityPr === void 0 ? void 0 : _gpcResponse$entityPr.preferenceCodeDetails) || [];
  return commCodes.map(code => {
    var _masterItem$COMMUN_TY;
    const masterItem = masterData[code];
    if (!masterItem) return null;
    const pref = gpcList.find(item => item.preferenceCode === code);
    return {
      code: masterItem.COMMUN_CD.trim(),
      title: masterItem.COMMUN_TITLE_TX.trim(),
      description: masterItem.COMMUN_DS_TX.trim(),
      type: (_masterItem$COMMUN_TY = masterItem.COMMUN_TYPE_CD) === null || _masterItem$COMMUN_TY === void 0 ? void 0 : _masterItem$COMMUN_TY.trim(),
      checked: pref ? pref.preferenceStatus === 'I' : true
    };
  }).filter(Boolean);
};

// ─── Function 8: buildSavePayload ────────────────────────────────────────────

export const buildSavePayload = (preferences = [], unsubscribeAll = false) => {
  const preferenceRequestGroup = preferences.map(item => ({
    preferenceCode: item.code,
    preferenceStatus: (() => {
      if (unsubscribeAll) return 'O';
      return item.checked ? 'I' : 'O';
    })(),
    preferenceReceiptTime: formatOptioTimestamp()
  }));
  if (unsubscribeAll) {
    preferenceRequestGroup.push({
      preferenceCode: 'MKTGBEML',
      preferenceStatus: 'O',
      preferenceReceiptTime: formatOptioTimestamp()
    });
  }
  return preferenceRequestGroup;
};
const shouldIncludeCard = card => {
  var _card$identifiers, _card$identifiers2, _card$platform, _card$status;
  if ((_card$identifiers = card.identifiers) !== null && _card$identifiers !== void 0 && _card$identifiers.is_purged) return false;
  if ((_card$identifiers2 = card.identifiers) !== null && _card$identifiers2 !== void 0 && _card$identifiers2.is_replaced) return false;
  const isoCode = (_card$platform = card.platform) === null || _card$platform === void 0 ? void 0 : _card$platform.iso_country_code;
  if (isoCode && isoCode !== '840') return false;
  const status = (_card$status = card.status) === null || _card$status === void 0 || (_card$status = _card$status.account_status) === null || _card$status === void 0 ? void 0 : _card$status[0];
  if (status && status !== 'Active') return false;
  return true;
};
const getConsumerOrBusiness = lineOfBusiness => {
  if (lineOfBusiness.includes('Corporate')) {
    return 'Corporate';
  }
  if (lineOfBusiness.includes('Business')) {
    return 'Business';
  }
  return 'Consumer';
};
const mapCardToHierarchy = card => {
  var _card$identifiers3, _card$product, _card$product2, _card$product3, _card$holder, _card$holder2;
  const isBasic = ((_card$identifiers3 = card.identifiers) === null || _card$identifiers3 === void 0 ? void 0 : _card$identifiers3.is_basic) !== false;
  const lineOfBusiness = ((_card$product = card.product) === null || _card$product === void 0 || (_card$product = _card$product.account_types) === null || _card$product === void 0 ? void 0 : _card$product.line_of_business_type) || 'ConsumerCard';
  const paymentType = ((_card$product2 = card.product) === null || _card$product2 === void 0 || (_card$product2 = _card$product2.account_types) === null || _card$product2 === void 0 ? void 0 : _card$product2.payment_type) || 'ChargeCard';
  const plasticType = ((_card$product3 = card.product) === null || _card$product3 === void 0 || (_card$product3 = _card$product3.account_types) === null || _card$product3 === void 0 || (_card$product3 = _card$product3.plastic_types) === null || _card$product3 === void 0 ? void 0 : _card$product3[0]) || 'Standard';
  const basicOrSupp = isBasic ? 'Basic' : 'Supp';
  const consumerOrBusiness = getConsumerOrBusiness(lineOfBusiness);
  const chargeOrLending = paymentType.includes('Lending') ? 'Lending' : 'Charge';
  return {
    relationshipId: card.account_token,
    productType: [basicOrSupp, consumerOrBusiness, chargeOrLending, plasticType, 'Standard'],
    isCorporate: consumerOrBusiness === 'Corporate',
    firstName: ((_card$holder = card.holder) === null || _card$holder === void 0 || (_card$holder = _card$holder.profile) === null || _card$holder === void 0 ? void 0 : _card$holder.first_name) || '',
    lastName: ((_card$holder2 = card.holder) === null || _card$holder2 === void 0 || (_card$holder2 = _card$holder2.profile) === null || _card$holder2 === void 0 ? void 0 : _card$holder2.last_name) || ''
  };
};

// ─── Function 9: processCardData ─────────────────────────────────────────────
/**
 * FIX: added purgedIndicator and US-only countryId filter,
 * matching Angular card-service.js getCardData() lines 56–61:
 *   !failureCard && !cancelledIndicator && !purgedIndicator && countryId === 'unitedstates'
 */
export const processCardData = cardResponse => {
  // Handle both array format (new Cards API) and legacy cardServiceResponseList format
  const cardList = Array.isArray(cardResponse) ? cardResponse : (cardResponse === null || cardResponse === void 0 ? void 0 : cardResponse.cardServiceResponseList) || [];
  return cardList.filter(shouldIncludeCard).map(mapCardToHierarchy);
};

// ─── Function 10: extractAccountTokens / extractDetokenizedAccounts ───────────

export const extractAccountTokens = accountResponse => {
  if (!(accountResponse !== null && accountResponse !== void 0 && accountResponse.account_details)) return [];
  return accountResponse.account_details.map((acc, index) => ({
    sequence: String(index + 1),
    value: acc.account_token,
    type: 'ACCOUNT'
  })).filter(item => item.value);
};
export const extractDetokenizedAccounts = detokenResponse => {
  if (!Array.isArray(detokenResponse)) return [];
  return detokenResponse.map(item => item.value);
};

// ─── Function 11: processCardEligibility ─────────────────────────────────────
/**
 * NEW — port of Angular processReturnList + getCompleteInfoOfAllEligibleNewsLetters.
 *
 * For each valid card:
 *   1. Look up its type hierarchy in the eligibility grid (getEligibleNewsLetters)
 *   2. Cross-reference against master data (getCompleteEligibleNewsLetters)
 *
 * Returns:
 *   {
 *     completeData: {
 *       [cardNumber]: masterDataEntry[],
 *       OptInNewsLetters: masterDataEntry[],
 *       totData: string[],              ← all eligible comm codes (flat, with dupes)
 *     },
 *     noOfCorpCards: number,
 *     cardNumbers: string[],
 *   }
 */
export const processCardEligibility = (cards, masterData) => {
  const eligibleLettersByCard = {};
  const cardNumbers = [];
  let noOfCorpCards = 0;
  cards.forEach(card => {
    // Angular always enters the grid from the Basic or Supp root node
    const rootKey = Array.isArray(card.productType) && card.productType[0] === 'Supp' ? 'Supp' : 'Basic';
    const eligibilityBase = {
      [rootKey]: ELIGIBILITY_GRID[rootKey]
    };
    const prodHierArr = Array.isArray(card.productType) ? card.productType : ['Basic'];
    const eligible = getEligibleNewsLetters(prodHierArr, eligibilityBase);
    eligibleLettersByCard[card.relationshipId] = eligible;
    cardNumbers.push(card.relationshipId);
    if (card.isCorporate) noOfCorpCards += 1;
  });
  const completeData = getCompleteEligibleNewsLetters(eligibleLettersByCard, masterData, cardNumbers);
  return {
    completeData,
    noOfCorpCards,
    cardNumbers
  };
};

// ─── Function 12: buildGPCPayloadForPFlow ────────────────────────────────────
/**
 * NEW — mirrors Angular lines 561–578.
 *
 * GPC payload = union(card-eligible codes from grid + URL-selected codes).
 * Angular rationale: eligible codes tell GPC "what this customer's cards can receive";
 * selected codes tell GPC "what the email link asked about" — both must be in one call.
 */
export const buildGPCPayloadForPFlow = (completeData, selectedCommCodes = []) => {
  const eligible = (completeData === null || completeData === void 0 ? void 0 : completeData.totData) || [];
  const combined = [...eligible, ...selectedCommCodes];
  return [...new Set(combined)];
};

// ─── Function 13: buildProductDisplayList ────────────────────────────────────
/**
 * Phase 1: annotate each card's product newsletters with GPC status.
 * Phase 2: flatten to a display list, applying corporate-card filtering:
 *   - noOfCorpCards === 0         → show all codes EXCEPT MKCORP
 *   - noOfCorpCards === numCards  → show ONLY MKCORP
 *   - mixed                       → show all codes
 * Phase 3: overlay GPC status onto URL-selected codes and ensure they appear.
 *
 * @param {object}   params                           parameters object
 * @param {object}   params.completeData              r
 * esult of processCardEligibility().completeData
 * @param {object[]} params.gpcList                   GPC REST response preferenceCodeDetails
 * @param {string}   params.emailAddress              decrypted email
 * @param {number}   params.noOfCorpCards             count of corporate cards
 * @param {object[]} params.selectedCommDetailsList   URL-requested codes with master data fields
 */
export const buildProductDisplayList = ({
  completeData,
  gpcList,
  emailAddress,
  noOfCorpCards,
  selectedCommDetailsList = []
}) => {
  if (!completeData) {
    return selectedCommDetailsList.map(s => ({
      ...s,
      communStatus: 'I'
    }));
  }

  // Empty gpcList is valid — GPC may have no stored preferences yet.
  // Still build the display list from card-eligible codes; all default to 'I'.
  const resolvedGpcList = gpcList || [];
  const cardKeys = Object.keys(completeData).filter(k => k !== 'OptInNewsLetters' && k !== 'totData');
  const numCardKeys = cardKeys.length;
  const allAreCorp = noOfCorpCards > 0 && noOfCorpCards === numCardKeys;
  const noCorp = noOfCorpCards === 0;

  // Phase 1: annotate per-card product newsletters with GPC status
  // Port of getCardBasedOptInCommunForSemiSecure → getToBeDisplayedProductList
  const annotated = {};
  cardKeys.forEach(cardKey => {
    const cardItems = completeData[cardKey]; // array of master-data objects
    annotated[cardKey] = (cardItems || []).map(eligible => {
      var _eligible$COMMUN_CD2, _eligible$COMMUN_DS_T, _eligible$COMMUN_TITL, _eligible$COMMUN_TYPE;
      const match = resolvedGpcList.find(opt => {
        var _opt$preferenceCode, _eligible$COMMUN_CD;
        return ((_opt$preferenceCode = opt.preferenceCode) === null || _opt$preferenceCode === void 0 ? void 0 : _opt$preferenceCode.trim().toLowerCase()) === ((_eligible$COMMUN_CD = eligible.COMMUN_CD) === null || _eligible$COMMUN_CD === void 0 ? void 0 : _eligible$COMMUN_CD.trim().toLowerCase());
      });
      return {
        communCode: (_eligible$COMMUN_CD2 = eligible.COMMUN_CD) === null || _eligible$COMMUN_CD2 === void 0 ? void 0 : _eligible$COMMUN_CD2.trim(),
        communDescription: (_eligible$COMMUN_DS_T = eligible.COMMUN_DS_TX) === null || _eligible$COMMUN_DS_T === void 0 ? void 0 : _eligible$COMMUN_DS_T.trim(),
        communTitle: (_eligible$COMMUN_TITL = eligible.COMMUN_TITLE_TX) === null || _eligible$COMMUN_TITL === void 0 ? void 0 : _eligible$COMMUN_TITL.trim(),
        communSampleLink: eligible.COMMUN_SAMP_LNK_TX,
        communTypeCode: (_eligible$COMMUN_TYPE = eligible.COMMUN_TYPE_CD) === null || _eligible$COMMUN_TYPE === void 0 ? void 0 : _eligible$COMMUN_TYPE.trim(),
        isCommunActive: eligible.COMMUN_STA_CD,
        // Angular: if email not null and GPC matched → use GPC status, else 'I'
        communStatus: match && emailAddress ? match.preferenceStatus : 'I',
        emailAddress
      };
    }).filter(nl => nl.isCommunActive !== 'HLD'); // exclude held comm codes
  });

  // Phase 2: flatten with corp-card filtering (port of convertToArrayList)
  const display = [];
  cardKeys.forEach(key => {
    (annotated[key] || []).forEach(item => {
      if (!item.communCode || item.communCode === 'O') return;
      const isMKCORP = item.communCode === 'MKCORP';
      if (noCorp && isMKCORP) return; // no corp cards → skip MKCORP
      if (allAreCorp && !isMKCORP) return; // all corp → only MKCORP

      const alreadyExists = display.some(d => {
        var _item$communCode;
        return d.communCode === ((_item$communCode = item.communCode) === null || _item$communCode === void 0 ? void 0 : _item$communCode.trim());
      });
      if (!alreadyExists) display.push(item);
    });
  });
  // Phase 3: overlay GPC status on URL-selected codes + ensure they appear in the list
  // Port of Angular lines 633–654
  selectedCommDetailsList.forEach(selectedComm => {
    const match = resolvedGpcList.find(opt => {
      var _opt$preferenceCode2, _selectedComm$communC;
      return ((_opt$preferenceCode2 = opt.preferenceCode) === null || _opt$preferenceCode2 === void 0 ? void 0 : _opt$preferenceCode2.trim().toLowerCase()) === ((_selectedComm$communC = selectedComm.communCode) === null || _selectedComm$communC === void 0 ? void 0 : _selectedComm$communC.trim().toLowerCase());
    });
    const updatedComm = match ? {
      ...selectedComm,
      communStatus: match.preferenceStatus
    } : selectedComm;
    const isPresent = display.some(d => {
      var _d$communCode, _updatedComm$communCo;
      return ((_d$communCode = d.communCode) === null || _d$communCode === void 0 ? void 0 : _d$communCode.trim()) === ((_updatedComm$communCo = updatedComm.communCode) === null || _updatedComm$communCo === void 0 ? void 0 : _updatedComm$communCo.trim());
    });
    if (!isPresent) {
      display.push(updatedComm);
    }
  });
  return display;
};

// ─── Legacy helpers kept for backward-compat ─────────────────────────────────

export const getEligibleProductCommCodes = masterData => Object.keys(masterData).filter(code => {
  var _masterData$code;
  return (_masterData$code = masterData[code]) === null || _masterData$code === void 0 || (_masterData$code = _masterData$code.COMMUN_TYPE_CD) === null || _masterData$code === void 0 ? void 0 : _masterData$code.startsWith('P');
});

/**
 * Kept as a simple fallback. When the real Cards API is integrated
 * use processCardEligibility + buildGPCPayloadForPFlow instead.
 */
export const getEligibleCommCodes = (masterData, cards = []) => {
  if (!masterData || cards.length === 0) return [];
  const {
    completeData
  } = processCardEligibility(cards, masterData);
  if (!completeData) return [];
  return [...new Set(completeData.totData)];
};